'use client'
// =============================================================================
// EventStream — widget CORE (right-panel) : flux d'événements réels du
// Mission Engine, via SSE /api/missions/events (mission-events.ts /
// mission.ws.ts côté API — jamais un 2e event bus, voir docs/architecture/
// command-center-ui.md, Modèle mental). Mapping type → libellé repris tel
// quel de l'ancien FooterRealtime.tsx (notifications retirées du footer lors
// de sa réécriture en status bar — ce widget leur donne un nouvel emplacement
// au lieu de perdre la logique).
// =============================================================================
import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'

type MissionEventType =
  | 'transition' | 'question' | 'answer' | 'planned' | 'split'
  | 'code:generated' | 'code:failed' | 'completed' | 'failed'

interface MissionEvent {
  type: MissionEventType
  missionId: string
  to?: string
  detail?: string
  at: string
}

const EVENT_MESSAGE: Record<MissionEventType, (e: MissionEvent) => string> = {
  completed:        () => 'Mission terminée ✔',
  failed:           () => 'Mission en échec ✕',
  'code:failed':    () => 'Génération de code échouée ✕',
  'code:generated': () => 'Code généré 📄',
  question:         () => 'Intervention requise ⚡',
  answer:           () => 'Réponse enregistrée',
  planned:          () => 'Plan de mission produit',
  split:            (e) => `Mission découpée${e.detail ? ` — ${e.detail}` : ''}`,
  transition:       (e) => `Mission → ${e.to ?? '?'}`,
}

const EVENT_COLOR: Record<MissionEventType, string> = {
  completed: '#34d399', failed: '#ef4444', 'code:failed': '#ef4444',
  question: '#f5a623', 'code:generated': '#00d4ff', answer: '#00d4ff',
  planned: '#00d4ff', split: '#00d4ff', transition: 'rgba(255,255,255,0.3)',
}

const MAX_EVENTS = 8

export function EventStream() {
  const { data: session } = useSession()
  const [events, setEvents] = useState<MissionEvent[]>([])

  useEffect(() => {
    if (!session) return
    const es = new EventSource('/api/missions/events')
    es.addEventListener('mission', (ev) => {
      try {
        const e = JSON.parse((ev as MessageEvent).data) as MissionEvent
        setEvents(prev => [e, ...prev].slice(0, MAX_EVENTS))
      } catch { /* ignore */ }
    })
    return () => es.close()
  }, [session])

  if (!session) return null

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.2)', marginBottom: 6, fontFamily: 'var(--font-mono)' }}>
        LIVE EVENTS
      </div>
      {events.length === 0 ? (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.15)', fontFamily: 'var(--font-mono)', fontStyle: 'italic' }}>
          Aucun événement — surveillance active
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {events.map((e, i) => {
            const color = EVENT_COLOR[e.type] ?? 'rgba(255,255,255,0.3)'
            const time = new Date(e.at).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
            return (
              <div key={`${e.missionId}-${e.at}-${i}`} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
                borderRadius: 6, background: `${color}0a`,
                fontFamily: 'var(--font-mono)', fontSize: 10,
              }}>
                <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 8, flexShrink: 0 }}>{time}</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {EVENT_MESSAGE[e.type]?.(e) ?? e.type}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
